import { Flex, Gutter, Loader } from '@gaiads/telia-react-component-library';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/reducers';
import { getAUser, initializeWebshopState, redirectToUrl, useQuery } from '../../utils';
import { shopClearOrderForOrganization, shopClearOrderOnBehalfUser, showGrowlNotification } from '../../store/actions';
import { useTranslation } from 'react-i18next';
import { User } from '../../types';

const WebshopLoader = () => {
  const dispatch = useDispatch();
  const queryParams = useQuery();
  const stateOrderingForOrganization = useSelector((state: RootState) => state.shop.orderForOrganization);
  const stateOrderingOnBehalfUserData = useSelector((state: RootState) => state.shop.orderOnBehalfUser);
  const user = useSelector((state: RootState) => state.authentication.user);
  const { t } = useTranslation('shop');

  const triggered = useRef(false);
  const [loading, setLoading] = useState(false);

  const queryParamOwnOrder = queryParams.get('ownOrder');
  const queryParamOnBehalfUserId = queryParams.get('onBehalfUserId');
  const queryParamOrderForOrganization = queryParams.get('orderForOrganization');

  /**
   * Cleanup logic to clear order-on-behalf state when component unmounts.
   * This prevents stale state data from being used if user navigates away from the webshop loader before redirection to webshop.
   */
  useEffect(() => {
    return () => {
      dispatch(shopClearOrderForOrganization());
      dispatch(shopClearOrderOnBehalfUser());
    };
  }, [dispatch]);

  useEffect(() => {
    // prevent concurrent initialization logic from executing
    if (triggered.current) return;

    const handleError = () => {
      dispatch(showGrowlNotification(t('externalWebshop.saveWebshopStateFailed'), 'error'));
      setLoading(false);
    };

    const initWebshop = (orderToUser: User | null) =>
      initializeWebshopState(orderToUser)
        .then(response => {
          if (response) {
            const url = new URL(process.env.REACT_APP_WEBSHOP_REDIRECT_URL!);
            url.searchParams.append('id', response.id);
            redirectToUrl(url.toString());
          } else {
            throw new Error('Null response on creating webshop state');
          }
        })
        .catch(handleError);

    triggered.current = true;

    setLoading(true);
    let processing = false;
    if (queryParamOwnOrder === 'true') {
      // user is ordering for themself
      initWebshop(user);
      processing = true;
    } else if (stateOrderingForOrganization || queryParamOrderForOrganization === 'true') {
      // we already have organization data in state, no need to fetch data again
      initWebshop(null);
      processing = true;
    } else if (stateOrderingOnBehalfUserData.status) {
      // we already have user data in state, no need to fetch data again
      initWebshop(stateOrderingOnBehalfUserData.userToOrderFor);
      processing = true;
    } else if (queryParamOnBehalfUserId) {
      // user id was passed via query param, we need to fetch user data first to initialize webshop state
      getAUser(queryParamOnBehalfUserId)
        .then(userData => {
          if (userData) {
            return userData;
          } else {
            throw new Error('User not found');
          }
        })
        .then(userData => initWebshop(userData))
        .catch(handleError);
      processing = true;
    }
    if (!processing) {
      // fallback to user ordering for themself
      initWebshop(user);
    }
  }, [
    dispatch,
    loading,
    queryParamOnBehalfUserId,
    queryParamOrderForOrganization,
    queryParamOwnOrder,
    stateOrderingForOrganization,
    stateOrderingOnBehalfUserData,
    t,
    triggered,
    user,
  ]);

  return loading ? (
    <>
      <Gutter size="xlg" />
      <Flex centered>
        <Loader />
      </Flex>
    </>
  ) : null;
};

export default WebshopLoader;
